import {
  ATTACK_BUTTONS,
  BUTTON,
  CHAIN_LIMIT,
  GRAB_RANGE,
  RISE_ATTACK,
  type AbilityDef,
  type AttackButton,
  type DirectionName,
  type InputBits,
} from "../sim";

/**
 * Пути бота к приёмам (Джон 05.11.2026: «Почему они используют не все приёмы? Я не видел ни разу захват и магию»):
 * не своя таблица кнопок, а то же описание ввода, что у игрока (content/abilities). Из ввода приёма — нажатия по
 * кадрам и условия; продолжение серии — поиском по дереву комбо от удара, который выходит сам. Новая способность
 * попадает к боту без отдельной правки.
 */

/** Направление от лица бойца (F — к сопернику) или N — ничего не держать. */
export type Hold = DirectionName | "N";

/** Кадр пути: что держать и что нажать (кнопки ударов, захват, издёвка). */
export interface Step {
  readonly dir: Hold;
  readonly press: InputBits;
}

/** Звено серии: когда идёт удар after — нажать button, держа dir. */
export interface Link {
  readonly after: string;
  readonly button: AttackButton;
  readonly dir: Hold;
}

/** Когда путь вообще годится. */
export interface Needs {
  /** Соперник не дальше / не ближе стольких пикселей. */
  readonly near?: number;
  readonly far?: number;
  /** Ярости не меньше. */
  readonly meter: number;
  /** Только сбитым на полу: атака при подъёме. */
  readonly lying: boolean;
  /** Издёвка: какая по счёту из издёвок набора — нажать на её кадре (sim tauntSlot). */
  readonly taunt?: { readonly slot: number; readonly count: number };
}

export interface Route {
  readonly id: string;
  readonly steps: readonly Step[];
  readonly chain: readonly Link[];
  readonly needs: Needs;
}

export const BUTTON_BITS: Readonly<Record<AttackButton, number>> = {
  lightPunch: BUTTON.lightPunch,
  heavyPunch: BUTTON.heavyPunch,
  lightKick: BUTTON.lightKick,
  heavyKick: BUTTON.heavyKick,
};

/** Касание направления в жесте: держать столько кадров, отпустить на столько. */
const TAP_HOLD = 2;
const TAP_GAP = 2;
/** Разгон до бега: «вперёд» держать столько кадров после двойного касания. */
const RUN_FRAMES = 10;
/** Отрыв: «вверх-вперёд» держать столько кадров. */
export const AIR_TAKEOFF = 2;
/** Прыжок героя от отрыва до земли, кадров (скорость 19, тяготение 1 — content/heroes). */
const AIR_TIME = 36;
/** Удар в воздухе становится активным за столько кадров до земли (иначе приземление его отменит). */
const AIR_MARGIN = 3;

/**
 * Прыжок вперёд: удар в воздухе — на спуске (на взлёте он проходит над головой), так, чтобы стать активным до земли:
 * через столько кадров после отрыва жать кнопку.
 */
export const airDelay = (ability: AbilityDef): number =>
  Math.max(0, AIR_TIME - AIR_TAKEOFF - ability.startup - AIR_MARGIN);
/** Заряд держат с запасом сверх порога. */
const CHARGE_SLACK = 4;
/** Захват мимо — соперник дальше зоны захвата хотя бы на столько. */
const WHIFF_MARGIN = 20;
/** Захват наверняка — ближе зоны на столько (бойцы сходятся не ближе 76 px, зона — 100). */
const GRAB_MARGIN = 5;

const repeat = (count: number, step: Step): Step[] => Array.from({ length: count }, () => step);
const idle = (count: number): Step[] => repeat(count, { dir: "N", press: 0 });

/** Нажатия по кадрам для приёма, который выходит сам (не продолжение серии); null — сам он не выходит. */
function directSteps(ability: AbilityDef): Step[] | null {
  const input = ability.input;
  if (input.series) return null;
  if (input.taunt) return [{ dir: "N", press: BUTTON.taunt }, ...idle(2)];
  if (input.grab || input.grabWhiff) return [{ dir: "N", press: BUTTON.grab }, ...idle(2)];
  if (input.gesture) {
    const taps = input.gesture.flatMap((dir) => [...repeat(TAP_HOLD, { dir, press: 0 }), ...idle(TAP_GAP)]);
    return [...taps, ...idle(2)];
  }
  const button = input.button;
  if (!button) return null;
  const bit = BUTTON_BITS[button] | (input.with ? BUTTON_BITS[input.with] : 0);
  const hold: Hold = input.hold ?? "N";
  const lead: Step[] = [];
  if (input.air) lead.push(...repeat(AIR_TAKEOFF, { dir: "UF", press: 0 }), ...idle(airDelay(ability)));
  if (input.run)
    lead.push(
      ...repeat(TAP_HOLD, { dir: "F", press: 0 }),
      ...idle(TAP_GAP),
      ...repeat(RUN_FRAMES, { dir: "F", press: 0 }),
    );
  for (const dir of input.motion ?? []) lead.push(...repeat(TAP_HOLD, { dir, press: 0 }));
  if (hold !== "N") lead.push(...repeat(2, { dir: hold, press: 0 }));
  const pressDir: Hold = input.run ? "F" : (input.motion?.at(-1) ?? hold);
  if (input.charge !== undefined)
    return [...lead, ...repeat(input.charge + CHARGE_SLACK, { dir: pressDir, press: bit }), ...idle(2)];
  return [...lead, { dir: pressDir, press: bit }, ...repeat(2, { dir: pressDir, press: 0 })];
}

/** Рёбра дерева комбо из приёма: кнопка, что держать, куда ведёт. */
function edges(ability: AbilityDef): { button: AttackButton; dir: Hold; to: string }[] {
  const out: { button: AttackButton; dir: Hold; to: string }[] = [];
  for (const button of ATTACK_BUTTONS) {
    const plain = ability.branches?.[button] ?? (button === ability.input.button ? ability.next : undefined);
    if (plain) out.push({ button, dir: "N", to: plain });
    const back = ability.backBranches?.[button];
    if (back) out.push({ button, dir: "B", to: back });
    const down = ability.downBranches?.[button];
    if (down) out.push({ button, dir: "D", to: down });
  }
  return out;
}

function needsOf(ability: AbilityDef, kit: readonly AbilityDef[]): Needs {
  const input = ability.input;
  const meter = ability.cost ?? 0;
  if (input.taunt) {
    const taunts = kit.filter((candidate) => candidate.input.taunt);
    return { meter, lying: false, taunt: { slot: taunts.indexOf(ability), count: taunts.length } };
  }
  if (input.grab) return { meter, lying: false, near: GRAB_RANGE - GRAB_MARGIN };
  if (input.grabWhiff) return { meter, lying: false, far: GRAB_RANGE + WHIFF_MARGIN };
  return { meter, lying: false, ...(input.near !== undefined ? { near: input.near - 5 } : {}) };
}

/**
 * Пути ко всем приёмам набора: прямые — из ввода; продолжения серий — кратчайшей цепочкой звеньев от прямого удара
 * (поиск в ширину по дереву); атака при подъёме — нажатием сбитым на полу.
 */
export function routesFor(kit: readonly AbilityDef[]): ReadonlyMap<string, Route> {
  const routes = new Map<string, Route>();
  const byId = new Map(kit.map((ability) => [ability.id, ability]));
  const frontier: Route[] = [];
  for (const ability of kit) {
    const steps = directSteps(ability);
    if (!steps) continue;
    const route: Route = { id: ability.id, steps, chain: [], needs: needsOf(ability, kit) };
    routes.set(ability.id, route);
    if (!ability.input.taunt && !ability.input.grab && !ability.input.grabWhiff) frontier.push(route);
  }
  // серии: удар за ударом, держа направление ветки; глубина — сколько звеньев позволяет мир (CHAIN_LIMIT)
  while (frontier.length > 0) {
    const from = frontier.shift();
    if (!from) break;
    const at = byId.get(from.id);
    if (!at || from.chain.length >= CHAIN_LIMIT) continue;
    for (const edge of edges(at)) {
      if (routes.has(edge.to) || !byId.has(edge.to)) continue;
      const route: Route = {
        id: edge.to,
        steps: from.steps,
        chain: [...from.chain, { after: at.id, button: edge.button, dir: edge.dir }],
        needs: from.needs,
      };
      routes.set(edge.to, route);
      frontier.push(route);
    }
  }
  // атака при подъёме: сбитый жмёт удар, пока лежит (sim/fighter.ts RISE_ATTACK)
  if (byId.has(RISE_ATTACK) && !routes.has(RISE_ATTACK))
    routes.set(RISE_ATTACK, {
      id: RISE_ATTACK,
      steps: [{ dir: "N", press: BUTTON.lightPunch }, ...idle(2)],
      chain: [],
      needs: { meter: 0, lying: true },
    });
  return routes;
}

/** Биты кадра пути: направление от лица бойца → стрелки экрана по взгляду. */
export function stepBits(step: Step, facing: number): InputBits {
  const forward = facing > 0 ? BUTTON.right : BUTTON.left;
  const back = facing > 0 ? BUTTON.left : BUTTON.right;
  const dir = step.dir;
  let bits = step.press;
  if (dir.includes("F")) bits |= forward;
  if (dir.includes("B")) bits |= back;
  if (dir.includes("D")) bits |= BUTTON.down;
  if (dir.includes("U")) bits |= BUTTON.up;
  return bits;
}
